import { CondicoesComerciasProps, SasPropostaProps } from '@app/api/sas/ordemCompra';
import { RelatorioHomologacaoProps } from '@app/api/sas/relatorioHomologacao';
import { ETipoDemanda } from '@app/enums/ETipoDemanda';
import {
  FormContext,
  FormDatePicker,
  FormInput,
  FormNumber,
  FormSelect,
  FormTextArea,
  notification,
} from '@digital/ui';
import { Collapse, DatePicker, InputNumber, Row, Select, Table, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import optionsMoedas from '@app/helpers/optionsMoedas';
import CurrencyInput from '@app/components/CurrencyInput';
import { parserCurrency, thousandSeparator } from '@app/helpers/stringHelper';
import { EGuidPerfilAcesso } from '@app/enums/EGuidPerfilAcesso';
import { getAllUsuariosByPerfilIdOptions } from '@app/api/usuario';
import { useRecoilState, useRecoilValue } from 'recoil';
import { parametrosSasState } from '@app/atoms';
import { Visibility } from '@app/components';
import FormRichText from '@app/components/RichText/FormRichText';
import { optionsSwitch, optionsTermoAditivo } from '@app/helpers/optionsSwitch';
import { isObjNotEmpty } from '@app/helpers/objHelper';
import { formatarDataPadrao } from '@app/helpers/dateTimeFormats';
import { ISasPropostaOrdemCompra } from '@app/api/sas/sasProposta';
import { dadosGeraisState, reloadVendorListState, vendorListState } from '../atoms';
import { EGuidStatusSasProposta } from '@app/enums/EGuidStatusSasProposta';
import { ETipoContrato } from '@app/enums/ETipoContrato';

const { Option } = Select;
const { Title } = Typography;
const { Panel } = Collapse;

interface FormAbaRelHomologacaoProps {
  dadosRelatorio: RelatorioHomologacaoProps;
  setNewDate: (values: any[]) => void;
  parteDoFluxo?: boolean;
  ehComprador: boolean;
}

const FormAbaRelHomologacao = ({
  dadosRelatorio,
  setNewDate,
  parteDoFluxo = false,
  ehComprador,
}: FormAbaRelHomologacaoProps) => {
  const { formProps } = useContext(FormContext);
  const parametrosSas = useRecoilValue(parametrosSasState);
  const [vendorList, setVendorList] = useRecoilState(vendorListState);
  const [reloadVendorList, setReloadVendorList] = useRecoilState(reloadVendorListState);

  const dadosGerais = useRecoilValue(dadosGeraisState);

  const [dataValues, setDataValues] = useState<any[]>([]);
  const [gerenteGeralSasOptions, setGerenteGeralSasOptions] = useState([]);
  const [gerenteDepartamentoSasOptions, setGerenteDepartamentoSasOptions] = useState([]);
  const [diretorComercialOptions, setDiretorComercialOptions] = useState([]);
  const [diretorEspecificoOptions, setDiretorEspecificoOptions] = useState([]);
  const [diretorPresidenteOptions, setDiretorPresidenteOptions] = useState([]);
  const ehPCS =
    dadosGerais.modalidadeContratoTotvs && dadosGerais.modalidadeContratoTotvs.toLowerCase() == ETipoContrato.PCS;

  useMemo(() => {
    getAllUsuariosByPerfilIdOptions(EGuidPerfilAcesso.GerenteGeralSas).then(res => {
      setGerenteGeralSasOptions(res);
    });

    getAllUsuariosByPerfilIdOptions(EGuidPerfilAcesso.GerenteDepartamentoSas).then(res => {
      setGerenteDepartamentoSasOptions(res);
    });

    getAllUsuariosByPerfilIdOptions(EGuidPerfilAcesso.DiretorComercial).then(res => {
      setDiretorComercialOptions(res);
    });

    getAllUsuariosByPerfilIdOptions(EGuidPerfilAcesso.DiretorEspecifico).then(res => {
      setDiretorEspecificoOptions(res);
    });

    getAllUsuariosByPerfilIdOptions(EGuidPerfilAcesso.DiretorPresidente).then(res => {
      setDiretorPresidenteOptions(res);
    });

    setReloadVendorList(!reloadVendorList);
  }, []);

  useEffect(() => {
    if ((dadosRelatorio.tipoOc === ETipoDemanda.NovaContratacao || dadosRelatorio.tipoOc === ETipoDemanda.FaturamentoDireto ) && dadosRelatorio.sasPropostas) {
      setDataValues(dadosRelatorio.sasPropostas);
    } else {
      setDataValues(dadosRelatorio.condicoesComerciais);
    }
  }, [dadosRelatorio]);

  useEffect(() => {
    setNewDate(dataValues);
  }, [dataValues]);

  useEffect(() => {
    if (!formProps!.values.ordemCompra && dadosRelatorio) {
      formProps!.setValues(dadosRelatorio);
    }
  }, [dadosRelatorio]);

  const columnsContratacao: ColumnProps<SasPropostaProps>[] = [
    {
      title: 'PROPONENTE',
      dataIndex: 'nomeSasFornecedor',
    },
    {
      title: 'RANKING',
      dataIndex: 'numeroVencedor',
      render: (_text, record) => {
        return dataValues.length > 0 ? <InputNumber disabled value={record.numeroVencedor} /> : <></>;
      },
    },
    {
      title: 'PROPOSTA INICIAL (A)',
      dataIndex: 'valorPropostaInicial',
      width: '175px',
      render: (text, record) => {
        return <CurrencyInput name="valorPropostaInicial" value={record.valorPropostaInicial} disabled />;
      },
    },
    {
      title: 'PROPOSTA EQUALIZADA',
      width: '175px',
      dataIndex: 'valorPropostaEqualizada',
      render: (text, record, index) => {
        return <CurrencyInput name="valorPropostaEqualizada" value={record.valorPropostaEqualizada} disabled />;
      },
    },
    {
      title: 'PROPOSTA FINAL (B)',
      width: '175px',
      dataIndex: 'valorPropostaComercial',
      render: (text, record, index) => {
        return <CurrencyInput name="valorPropostaComercial" value={record.valorPropostaComercial} disabled />;
      },
    },
    {
      title: 'VARIAÇÃO PROPOSTA (R$) (C = A - B)',
      dataIndex: 'variacao',
      width: '175px',
      render: (_text, record) => {
        return <CurrencyInput name="variacao" value={record.variacao} disabled />;
      },
    },
    {
      title: 'VARIAÇÃO PROPOSTA (%) (D = C / A)',
      dataIndex: 'variacaoPerc',
      width: '100px',
      render: (_text, record) => {
        return (
          <InputNumber
            name="variacaoPerc"
            value={record.variacaoPerc}
            // decimalSeparator=","
            disabled
          />
        );
      },
    },
    {
      title: 'VARIAÇÃO OC (R$) (E = VALOR OC - B)',
      dataIndex: 'difOrcado',
      width: '175px',
      render: (_text, record) => {
        return <CurrencyInput name="difOrcado" value={record.difOrcado} disabled />;
      },
    },
    {
      title: 'VARIAÇÃO OC (%) (F = E / valor da OC)',
      dataIndex: 'difPropostaPerc',
      width: '100px',
      render: (_text, record) => {
        return <InputNumber name="difPropostaPerc" value={record.difPropostaPerc} decimalSeparator="," disabled />;
      },
    },
    {
      title: 'COND. PAGTO',
      dataIndex: 'condicaoPagamento',
      // render: (text, _record, index) => (
      //   <FormInput
      //     disabled
      //     value={text}
      //     name="condicaoPagamento"
      //   />
      // ),
    },
    {
      title: 'PARECER TÉCNICO',
      dataIndex: 'parecerTecnico',
      // render: (text, _record, index) => (
      //   <FormInput
      //     disabled
      //     value={text}
      //     name='parecerTecnico'
      //     span={3}
      //   />
      // ),
    },
    {
      title: 'AVAL. FINANCEIRA',
      dataIndex: 'avaliacaoFinanceira',
      // render: (text, _record, index) => (
      //   <FormInput
      //     disabled
      //     value={text}
      //     name='avaliacaoFinanceira'
      //   />
      // ),
    },
    {
      title: 'DDI',
      dataIndex: 'avaliacaoDdi',
      // width: 120,
      // render: (text, record, index) => (
      //   <FormInput
      //     disabled
      //     value={text}
      //     name='avaliacaoDdi'
      //   />
      // ),
    },
    {
      title: 'id',
      dataIndex: 'id',
      render: (_t, _r, i) => {
        return String(i + 1);
      },
      width: 0,
      className: 'not-display',
    },
  ];

  const columnsFaturamentoDireto: ColumnProps<SasPropostaProps>[] = [
    {
      title: 'PROPONENTE',
      dataIndex: 'nomeSasFornecedor',
    },
    {
      title: 'RANKING',
      dataIndex: 'numeroVencedor',
      render: (_text, record) => {
        return dataValues.length > 0 ? <InputNumber disabled value={record.numeroVencedor} /> : <></>;
      },
    },
    {
      title: 'PROPOSTA INICIAL (A)',
      dataIndex: 'valorPropostaInicial',
      width: '175px',
      render: (text, record) => {
        return <CurrencyInput name="valorPropostaInicial" value={record.valorPropostaInicial} disabled />;
      },
    },
    {
      title: 'PROPOSTA EQUALIZADA',
      width: '175px',
      dataIndex: 'valorPropostaEqualizada',
      render: (text, record, index) => {
        return <CurrencyInput name="valorPropostaEqualizada" value={record.valorPropostaEqualizada} disabled />;
      },
    },
    {
      title: 'PROPOSTA FINAL (B)',
      width: '175px',
      dataIndex: 'valorPropostaComercial',
      render: (text, record, index) => {
        return <CurrencyInput name="valorPropostaComercial" value={record.valorPropostaComercial} disabled />;
      },
    },
    {
      title: 'VARIAÇÃO PROPOSTA (R$) (C = A - B)',
      dataIndex: 'variacao',
      width: '175px',
      render: (_text, record) => {
        return <CurrencyInput name="variacao" value={record.variacao} disabled />;
      },
    },
    {
      title: 'VARIAÇÃO PROPOSTA (%) (D = C / A)',
      dataIndex: 'variacaoPerc',
      width: '100px',
      render: (_text, record) => {
        return (
          <InputNumber
            name="variacaoPerc"
            value={record.variacaoPerc}
            // decimalSeparator=","
            disabled
          />
        );
      },
    },
    {
      title: 'VARIAÇÃO OC (R$) (E = VALOR OC - B)',
      dataIndex: 'difOrcado',
      width: '175px',
      render: (_text, record) => {
        return <CurrencyInput name="difOrcado" value={record.difOrcado} disabled />;
      },
    },
    {
      title: 'VARIAÇÃO OC (%) (F = E / valor da OC)',
      dataIndex: 'difPropostaPerc',
      width: '100px',
      render: (_text, record) => {
        return <InputNumber name="difPropostaPerc" value={record.difPropostaPerc} decimalSeparator="," disabled />;
      },
    },
    {
      title: 'COND. PAGTO',
      dataIndex: 'condicaoPagamento',
      // render: (text, _record, index) => (
      //   <FormInput
      //     disabled
      //     value={text}
      //     name="condicaoPagamento"
      //   />
      // ),
    },
    {
      title: 'PARECER TÉCNICO',
      dataIndex: 'parecerTecnico',
      // render: (text, _record, index) => (
      //   <FormInput
      //     disabled
      //     value={text}
      //     name='parecerTecnico'
      //     span={3}
      //   />
      // ),
    },
    {
      title: 'AVAL. FINANCEIRA',
      dataIndex: 'avaliacaoFinanceira',
      // render: (text, _record, index) => (
      //   <FormInput
      //     disabled
      //     value={text}
      //     name='avaliacaoFinanceira'
      //   />
      // ),
    },
    {
      title: 'DDI',
      dataIndex: 'avaliacaoDdi',
      // width: 120,
      // render: (text, record, index) => (
      //   <FormInput
      //     disabled
      //     value={text}
      //     name='avaliacaoDdi'
      //   />
      // ),
    },
    {
      title: 'id',
      dataIndex: 'id',
      render: (_t, _r, i) => {
        return String(i + 1);
      },
      width: 0,
      className: 'not-display',
    },
  ];

  const columnsAditivo: ColumnProps<CondicoesComerciasProps>[] = [
    {
      title: 'DESCRIÇÃO',
      dataIndex: 'descricao',
    },
    {
      title: 'VALOR TOTAL ACUMULADO',
      dataIndex: 'valorTotalAcumulado',
      render: (text, record) => {
        return <InputNumber value={Number(text)} formatter={e => Number(e).toLocaleString('pt-BR')} disabled />;
      },
    },
    {
      title: 'VALOR DO ADITIVO',
      dataIndex: 'valorAditivo',
      render: (text, record, index) => {
        return (
          <FormNumber
            formatter={e => thousandSeparator(e)}
            parser={e => parserCurrency(e)}
            onChange={e => {
              if (e) {
                let valueInfo;
                const newData = [...dataValues];
                if (String(e).length === 0) {
                  valueInfo = 0;
                }
                if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
                  valueInfo = e;
                }
                valueInfo = Number(e.toFixed(2));
                if (index > -1) {
                  const item = newData[index];
                  newData.splice(index, 1, {
                    ...item,
                    valorAditivo: valueInfo,
                  });
                  setDataValues(newData);
                }
              }
            }}
            name="valorAditivoUltimoTac"
            step={0.01}
            disabled
            value={text}
          />
        );
      },
    },
    {
      title: 'VARIAÇÃO DO CT ORIGINAL (%)',
      dataIndex: 'variacaoCtOriginal',
      render: (_text, record) => {
        return <InputNumber value={record.variacaoCtOriginal} decimalSeparator="," formatter={e => `${e}%`} disabled />;
      },
    },
    {
      title: 'INICIO DE VIGÊNCIA',
      dataIndex: 'inicioVigencia',
      render: (text, record) => {
        return <DatePicker value={moment(text, 'YYYY-MM-DD')} format={'DD/MM/YYYY'} disabled />;
      },
    },
    {
      title: 'TÉRMINO DE VIGÊNCIA',
      dataIndex: 'terminoVigencia',
      render: (text, record, i) => {
        return (
          <FormDatePicker
            value={moment(text, 'YYYY-MM-DD')}
            name="terminoVigencia"
            label=""
            format={'DD/MM/YYYY'}
            onChange={e => {
              const newData = [...dataValues];
              var index = 1;
              if (newData.length > 2) {
                index = newData.findIndex(
                  item => record.aditivoId === item.aditivoId && record.contratoId === item.contratoId,
                );
              }

              if (index > -1) {
                const item = newData[index];
                newData.splice(index, 1, {
                  ...item,
                  terminoVigencia: moment(e).format('YYYY-MM-DD'),
                });
              }

              setDataValues(newData);
            }}
            disabled
          />
        );
      },
    },
    {
      title: 'PRAZO TOTAL ACUMULADO',
      dataIndex: 'prazoAcumulado',
      render: text => {
        return <InputNumber style={{ width: 70 }} value={Number(text)} disabled />;
      },
    },
    {
      title: 'VARIAÇÃO DO PRAZO',
      dataIndex: 'variacaoPrazo',
      render: text => {
        return <InputNumber style={{ width: 70 }} value={Number(text)} disabled />;
      },
    },
    {
      title: 'ESCOPO',
      dataIndex: 'escopo',
    },
    {
      title: 'id',
      dataIndex: 'id',
      render: (_text, record) => {
        return `${record.contratoId}${record.aditivoId}`;
      },
      width: 0,
      className: 'not-display',
    },
  ];

  const columnsInformacoesRelevantes = useMemo(() => {
    const columns: ColumnProps<ISasPropostaOrdemCompra>[] = ([] = new Array());

    columns.push({
      title: 'parecer técnico',
      align: 'center',
      render: (text, record) => {
        return (
          <Visibility visible={isObjNotEmpty(record.statusResultadoParecerTecnico)}>
            <span>{record.statusResultadoParecerTecnico}</span>
          </Visibility>
        );
      },
    });

    columns.push({
      title: 'Avaliação Financeira',
      align: 'center',
      render: (text, record) =>
        record.resultadoCadastroResultadoAvaliacaoFinanceira ? (
          <span>{record.resultadoCadastroResultadoAvaliacaoFinanceira}</span>
        ) : (
          dadosGerais.statusCodigo >= 19 && <span>Não Enviada</span>
        ),
    });

    columns.push({
      // title: "Resultado do Parecer do DPC",
      title: 'DDI',
      align: 'center',
      render: (text, record) => (
        <Visibility visible={record.statusSasPropostaId == EGuidStatusSasProposta.Ativa}>
          <span>{record.statusResultadoEnvioDdi}</span>
        </Visibility>
      ),
    });

    columns.push({
      title: 'Governança de Contratos',
      align: 'center',
      render: (text, record) => (
        <Visibility visible={record.statusSasPropostaId == EGuidStatusSasProposta.Ativa}>
          <span>{dadosRelatorio.sumario.eligibilidadeGovernancaAprovacao}</span>
        </Visibility>
      ),
    });

    return columns;
  }, [vendorList]);

  const calcVariacaoPorcentagem = (a: number, b: number): string => {
    if (a && b) {
      return Math.abs(((Number(a) - Number(b)) / Number(b)) * 100).toFixed(2);
    }
    return '0';
  };

  return (
    <>
      <Row>
        <FormInput name="statusAprovacao" label="Status" span={2} disabled />
        <Visibility visible={dadosRelatorio.tipoOc === ETipoDemanda.NovaContratacao}>
          <FormSelect
            options={[
              { value: 'Contrato', text: 'Contrato' },
              { value: 'PCS', text: 'PCS' },
            ]}
            name="modalidadeContratoTotvs"
            label="Modalidade Contrato Totvs"
            onChange={e => {
              if (e === 'PCS') {
                notification.warn('Número de contrato preenchido!', 'Atenção!');
                formProps!.setFieldValue('contrato', undefined);
              }
              formProps!.setFieldValue('modalidadeContratoTotvs', e);
            }}
            span={2}
            required
            disabled={parteDoFluxo || !ehComprador || dadosRelatorio.tipoOc === ETipoDemanda.NovaContratacao}
          />
        </Visibility>

        <Visibility visible={dadosRelatorio.tipoOc === ETipoDemanda.Aditivo}>
          <FormSelect
            mode="multiple"
            label="Característica do Aditivo"
            name="tipoAditivo"
            span={2}
            disabled
            value={((dadosRelatorio.tipoAditivo && dadosRelatorio.tipoAditivo) || '').split('\\')}
          />
          <FormDatePicker name="dataInicioAditivo" format="DD/MM/YYYY" disabled span={2} label="Data Início Aditivo" />
          <FormDatePicker
            name="dataTerminoAditivo"
            format="DD/MM/YYYY"
            disabled
            span={2}
            label="Data Término Aditivo"
            required
          />
        </Visibility>
      </Row>

      <Row>
        <FormInput name="numeroOc" label="Ordem de Compra" disabled span={2} />
        {dadosRelatorio.tipoOc === ETipoDemanda.FaturamentoDireto ? (
          <FormInput
            name="contrato"
            label={ehPCS ? 'PCS' : 'Contrato'}
            disabled={
              parteDoFluxo ||
              !ehComprador ||
              dadosRelatorio.tipoOc === ETipoDemanda.Aditivo ||
              formProps!.values.modalidadeContratoTotvs === 'PCS'
            }
            required={formProps && formProps.values.modalidadeContratoTotvs === 'Contrato'}
            span={2}
          />
        ) : (
          <FormInput
            name="contrato"
            label={ehPCS ? 'PCS' : 'Contrato'}
            disabled={
              parteDoFluxo ||
              !ehComprador ||
              dadosRelatorio.tipoOc === ETipoDemanda.Aditivo ||
              formProps!.values.modalidadeContratoTotvs === 'PCS'
            }
            required={formProps && formProps.values.modalidadeContratoTotvs === 'Contrato'}
            span={2}
          />
        )}
        <Visibility visible={dadosRelatorio.tipoOc == ETipoDemanda.NovaContratacao}>
          <FormInput name="caracteristicaOc" label="Carac. da Ordem de Compra" disabled span={2} />
        </Visibility>
        <Visibility visible={dadosRelatorio.tipoOc == ETipoDemanda.Aditivo}>
          <FormSelect
            options={optionsTermoAditivo}
            name="termoAditivo"
            label="Termo Aditivo"
            span={2}
            required
            disabled
          />
        </Visibility>
        <FormInput name="analistaSuprimentos" label="Analista de Suprimentos" disabled span={2} />
        <FormInput name="solicitante" label="Solicitante" disabled span={2} />
        <FormInput name="areaRequisitante" label="Área Requisitante" disabled span={2} />
        <Visibility visible={dadosRelatorio.tipoOc != ETipoDemanda.FaturamentoDireto}>
          <FormInput name="previstoOrcamento" label="Previsto no Orçamento" disabled span={2} />
        </Visibility>
        <FormNumber
          formatter={e => thousandSeparator(e)}
          parser={e => parserCurrency(e)}
          onChange={e => {
            if (e) {
              if (String(e).length === 0) {
                return formProps!.setFieldValue('orcamento', '0.00');
              }
              if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
                return formProps!.setFieldValue('orcamento', e);
              }
              return formProps!.setFieldValue('orcamento', Number(e.toFixed(2)));
            }
          }}
          name="orcamento"
          label="Valor OC (R$)"
          step={0.01}
          span={2}
          disabled
        />
        <FormSelect
          options={optionsMoedas}
          name="moeda"
          label="Moeda"
          span={2}
          required
          disabled={parteDoFluxo || !ehComprador}
          defaultValue={'0'}
        />
        <Visibility visible={dadosRelatorio.tipoOc != ETipoDemanda.FaturamentoDireto}>
          <FormInput
            name="naturezaContabil"
            label="Natureza Contábil"
            span={2}
            disabled={parteDoFluxo || !ehComprador}
          />
        </Visibility>
        {/* <FormInput name="codigoMensagemContratoTotvs" label="Código Mensagem no Totvs" span={2} disabled={parteDoFluxo || !ehComprador} /> */}
      </Row>

      <Row>
        <FormTextArea
          id="mrn-textarea-objetoASerContratado"
          name="objetoASerContratado"
          label="Objeto a Ser Contratado"
          rows={4}
          span={12}
          autosize={{ minRows: 4, maxRows: 8 }}
          disabled={parteDoFluxo || !ehComprador}
        />
        <FormTextArea
          id="mrn-textarea-justificativaContratacao"
          name={'justificativaContratacao'}
          label="Justificativa de Contratação"
          rows={4}
          span={12}
          autosize={{ minRows: 4, maxRows: 8 }}
          disabled={parteDoFluxo || !ehComprador}
        />
      </Row>
      <Title level={4}>Condições Comerciais</Title>
      <Row>
        {dadosRelatorio.tipoOc && (
          <Table
            dataSource={dataValues}
            columns={
              dadosRelatorio.tipoOc === ETipoDemanda.NovaContratacao
                ? columnsContratacao
                : dadosRelatorio.tipoOc === ETipoDemanda.FaturamentoDireto
                ? columnsFaturamentoDireto
                : columnsAditivo
            }
            rowKey="id"
          />
        )}
      </Row>
      <Title level={4}>Informações Ordem de Compra / Negociação</Title>

      <Row>
        <Visibility visible={dadosRelatorio && dadosRelatorio.tipoOc === ETipoDemanda.Aditivo}>
          <FormNumber
            formatter={e => thousandSeparator(e)}
            parser={e => parserCurrency(e)}
            onChange={e => {
              if (String(e).length === 0) {
                return formProps!.setFieldValue('valorPropostaPremiada', '0.00');
              }
              if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
                return formProps!.setFieldValue('valorPropostaPremiada', e);
              }
              return formProps!.setFieldValue('valorPropostaPremiada', Number(e.toFixed(2)));
            }}
            name="valorPropostaPremiada"
            label="Valor TAC (R$) (A)"
            step={0.01}
            span={3}
            disabled
          />
        </Visibility>

        <Visibility visible={dadosRelatorio && ( dadosRelatorio.tipoOc === ETipoDemanda.NovaContratacao || dadosRelatorio.tipoOc === ETipoDemanda.FaturamentoDireto)}>
          <FormNumber
            formatter={e => thousandSeparator(e)}
            parser={e => parserCurrency(e)}
            onChange={e => {
              if (String(e).length === 0) {
                return formProps!.setFieldValue('valorAContratar', '0.00');
              }
              if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
                return formProps!.setFieldValue('valorAContratar', e);
              }
              return formProps!.setFieldValue('valorAContratar', Number(e.toFixed(2)));
            }}
            name={'valorAContratar'}
            label="Valor a Contratar (R$) (A)"
            step={0.01}
            span={3}
            disabled
          />
        </Visibility>

        <FormNumber
          formatter={e => thousandSeparator(e)}
          parser={e => parserCurrency(e)}
          onChange={e => {
            if (String(e).length === 0) {
              return formProps!.setFieldValue('orcamento', '0.00');
            }
            if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
              return formProps!.setFieldValue('orcamento', e);
            }
            return formProps!.setFieldValue('orcamento', Number(e.toFixed(2)));
          }}
          name="orcamento"
          label="Ordem de compra (R$) (B)"
          step={0.01}
          span={3}
          disabled
        />
        <FormNumber
          formatter={e => thousandSeparator(e)}
          parser={e => parserCurrency(e)}
          onChange={e => {
            if (String(e).length === 0) {
              return formProps!.setFieldValue('savingOrcamento', '0.00');
            }
            if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
              return formProps!.setFieldValue('savingOrcamento', e);
            }
            return formProps!.setFieldValue('savingOrcamento', Number(e.toFixed(2)));
          }}
          name="savingOrcamento"
          label="Variação Ordem de compra (R$) (C = B - A)"
          step={0.01}
          span={3}
          disabled
        />
        <FormNumber
          formatter={e => thousandSeparator(e)}
          // parser={e => parserCurrency(e)}
          onChange={e => {
            if (String(e).length === 0) {
              return formProps!.setFieldValue('savingOrcamentoPerc', '0.00');
            }
            if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
              return formProps!.setFieldValue('savingOrcamentoPerc', e);
            }
            return formProps!.setFieldValue('savingOrcamentoPerc', Number(e.toFixed(2)));
          }}
          name="savingOrcamentoPerc"
          label={
            dadosRelatorio && ( dadosRelatorio.tipoOc === ETipoDemanda.NovaContratacao || dadosRelatorio.tipoOc === ETipoDemanda.FaturamentoDireto)
              ? 'Variação Ordem de Compra (%) (D = C / B)'
              : 'Variação Ordem de Compra (%) (D = A / B - 1)'
          }
          step={0.01}
          span={3}
          disabled
        />
      </Row>
      <Row>
        <Visibility visible={dadosRelatorio && ( dadosRelatorio.tipoOc === ETipoDemanda.NovaContratacao || dadosRelatorio.tipoOc === ETipoDemanda.FaturamentoDireto)}>
          <FormNumber
            formatter={e => thousandSeparator(e)}
            parser={e => parserCurrency(e)}
            onChange={e => {
              if (String(e).length === 0) {
                return formProps!.setFieldValue('menorPcEqualizada', '0.00');
              }
              if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
                return formProps!.setFieldValue('menorPcEqualizada', e);
              }
              return formProps!.setFieldValue('menorPcEqualizada', Number(e.toFixed(2)));
            }}
            name="menorPcEqualizada"
            label="Menor PC. Equalizada (E)"
            step={0.01}
            span={3}
            disabled
          />
        </Visibility>

        <Visibility visible={dadosRelatorio && dadosRelatorio.tipoOc === ETipoDemanda.Aditivo}>
          <FormNumber
            formatter={e => thousandSeparator(e)}
            parser={e => parserCurrency(e)}
            onChange={e => {
              if (String(e).length === 0) {
                return formProps!.setFieldValue('valorPropostaInicial', '0.00');
              }
              if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
                return formProps!.setFieldValue('valorPropostaInicial', e);
              }
              return formProps!.setFieldValue('valorPropostaInicial', Number(e.toFixed(2)));
            }}
            name="valorPropostaInicial"
            label="Proposta Inicial (R$) (E)"
            step={0.01}
            span={3}
            disabled
          />
        </Visibility>

        <FormNumber
          formatter={e => thousandSeparator(e)}
          parser={e => parserCurrency(e)}
          onChange={e => {
            if (e) {
              if (String(e).length === 0) {
                return formProps!.setFieldValue('savingNegociacao', '0.00');
              }
              if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
                return formProps!.setFieldValue('savingNegociacao', e);
              }
              return formProps!.setFieldValue('savingNegociacao', Number(e.toFixed(2)));
            }
          }}
          name="savingNegociacao"
          label="Variação Negociação (R$) (F = E - A)"
          step={0.01}
          span={3}
          disabled
        />
        <FormNumber
          formatter={e => thousandSeparator(e)}
          // parser={e => parserCurrency(e)}
          onChange={e => {
            if (String(e).length === 0) {
              return formProps!.setFieldValue('savingNegociacaoPerc', '0.00');
            }
            if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
              return formProps!.setFieldValue('savingNegociacaoPerc', e);
            }
            return formProps!.setFieldValue('savingNegociacaoPerc', Number(e.toFixed(2)));
          }}
          name="savingNegociacaoPerc"
          label={
            dadosRelatorio && ( dadosRelatorio.tipoOc === ETipoDemanda.NovaContratacao || dadosRelatorio.tipoOc === ETipoDemanda.FaturamentoDireto)
              ? 'Variação Negociação (%) (G = F / E)'
              : 'Variação Negociação (%) (G = A / E – 1)'
          }
          step={0.01}
          span={3}
          disabled
        />
      </Row>
      <Title level={4}>Resultado do Processo de Compra</Title>
      <Row>
        <FormInput name="nomePropostaPremiada" label="Empresa Premiada" disabled span={3} />
        <FormNumber
          formatter={e => thousandSeparator(e)}
          parser={e => parserCurrency(e)}
          onChange={e => {
            if (e) {
              if (String(e).length === 0) {
                return formProps!.setFieldValue('valorAContratar', '0.00');
              }
              if (String(e).split('.')[1] && String(e).split('.')[1].length === 0) {
                return formProps!.setFieldValue('valorAContratar', e);
              }
              return formProps!.setFieldValue('valorAContratar', Number(e.toFixed(2)));
            }
          }}
          name="valorAContratar"
          label="Valor Total"
          step={0.01}
          span={3}
          disabled={parteDoFluxo || !ehComprador || (dadosRelatorio && dadosRelatorio.tipoOc === ETipoDemanda.Aditivo)}
        />
        <FormNumber
          name="prazoExecucao"
          label="Prazo de Vigência (meses)"
          span={3}
          disabled={parteDoFluxo || !ehComprador || (dadosRelatorio && dadosRelatorio.tipoOc === ETipoDemanda.Aditivo)}
        />
      </Row>
      <Title level={4}>Histórico de Processo de Compra</Title>
      <Row>
        <FormRichText
          name="historicoProcessoCompra"
          label=""
          disabled={parteDoFluxo || !ehComprador}
          readonly={parteDoFluxo || !ehComprador}
          initialValue={dadosRelatorio.historicoProcessoCompra}
        />
      </Row>
      <Visibility visible={dadosRelatorio && dadosRelatorio.tipoOc === ETipoDemanda.Aditivo}>
        <Title level={4}>Informações Relevantes</Title>
        <Row>
          <Table
            className="tabela-vendor-list-info"
            columns={columnsInformacoesRelevantes}
            dataSource={vendorList}
            rowKey={'id'}
            pagination={false}
            rowClassName={(record: any) => ''}
            style={{ marginBottom: 10 }}
          />

          <Visibility visible={parteDoFluxo || ehComprador}>
            <FormSelect
              options={optionsSwitch}
              name="haReajuste"
              label="Há Reajuste"
              onChange={e => {
                formProps!.setFieldValue('haReajuste', e);
              }}
              span={2}
              required
              disabled={!ehComprador}
            />

            <Visibility visible={formProps && formProps!.values.haReajuste == true}>
              <FormSelect
                options={[
                  { value: 'f0c2d83e-1704-475c-85a2-0084363ef4d9', text: 'Contratos de Serviços' },
                  { value: 'f3d8e7db-8ea2-4239-8fc8-16f34bc2639e', text: 'Locação' },
                  { value: '4e23edfb-13cb-4f31-8582-298f10b2a872', text: 'Software' },
                ]}
                name="tipoServicoRH"
                label="Tipo de Serviço"
                onChange={e => {
                  formProps!.setFieldValue('tipoServicoRH', e);
                }}
                span={2}
                required
                disabled={!ehComprador}
              />
            </Visibility>
          </Visibility>
        </Row>
      </Visibility>

      <Title level={4}>Responsáveis pela Validação</Title>
        <Row>
          <Visibility
            visible={
              parametrosSas.sasValorMaximoGerenteDepartamentoHabilitado &&
              dadosRelatorio.valorCalcRH >= parametrosSas.sasValorMinimoRealizacaoRH
            }>
            <FormSelect
              name="gerenteDepartamentoAquisicaoServicosId"
              label="Gerente Departamento Aquisição de Serviços"
              span={6}
              disabled={parteDoFluxo || !ehComprador}
              options={gerenteDepartamentoSasOptions}
            />
          </Visibility>

          <Visibility
            visible={
              parametrosSas.sasValorMaximoGerenteGeralHabilitado &&
              dadosRelatorio.valorCalcRH >= parametrosSas.sasValorMinimoRealizacaoRH &&
              (parametrosSas.sasValorMaximoGerenteDepartamentoHabilitado === false ||
                dadosRelatorio.valorCalcRH >= parametrosSas.sasValorMaximoGerenteDepartamento)
            }>
            <FormSelect
              name="gerenteGeralAquisicaoServicosId"
              label="Gerente Geral de Aquisições de Serviços e Administração de Contratos"
              span={6}
              disabled={parteDoFluxo || !ehComprador}
              options={gerenteGeralSasOptions}
            />
          </Visibility>

          <Visibility visible={parametrosSas.sasValorMaximoGerenteGeral < dadosRelatorio.valorCalcRH && dadosRelatorio.tipoOc != ETipoDemanda.FaturamentoDireto}>
            <FormSelect
              name="diretorEspecificoId"
              label="Diretor Específico"
              span={4}
              disabled={parteDoFluxo || !ehComprador}
              options={diretorEspecificoOptions}
              allowClear
            />
            <FormSelect
              name="diretorComercialId"
              label="Diretoria Comercial"
              span={4}
              disabled={parteDoFluxo || !ehComprador}
              options={diretorComercialOptions}
            />
            <FormSelect
              name="diretorPresidenteId"
              label="Diretor Presidente"
              span={4}
              disabled={parteDoFluxo || !ehComprador}
              options={diretorPresidenteOptions}
            />
          </Visibility>
        </Row>
    </>
  );
};

export default FormAbaRelHomologacao;
